import * as React from "react";
import { useState } from "react";
import { useParams, useNavigate } from "react-router";
import { ulid } from "ulid";
import { toast } from "sonner";
import type { JSX } from "react";
import type { CompleteCookSessionRequest, CookSessionApiItem, RecipeApiItem } from "@recipevault/shared";

import { useAuth } from "../auth/AuthProvider.js";
import { useRecipe } from "../recipes/useRecipe.js";
import { useCookSession } from "./useCookSession.js";
import { useTimers } from "./useTimers.js";
import { useWakeLock } from "./useWakeLock.js";
import { usePrefs } from "./usePrefs.js";
import { clearFocusedSessionId } from "./focusedSession.js";
import { progressOf } from "./progress.js";
import { nextSplit, SplitDivider } from "./SplitDivider.js";
import { ProgressBar } from "./ProgressBar.js";
import { PrepPanel } from "./PrepPanel.js";
import { StepPane } from "./StepPane.js";
import { IngredientsPane } from "./IngredientsPane.js";
import { TimerPill } from "./TimerPill.js";
import { TimerTray } from "./TimerTray.js";
import { FiredAlarmStack } from "./FiredAlarmStack.js";
import { CookBottomBar } from "./CookBottomBar.js";
import { CookMenu } from "./CookMenu.js";
import { WaitPicker } from "./WaitPicker.js";
import { CompletionModal } from "./CompletionModal.js";
import { CookErrorBoundary } from "./CookErrorBoundary.js";
import { sync } from "../../sync/SyncService.js";
import { markSlotCooked } from "../meal-plans/planLinkage.js";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const nowIso = (): string => new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

// ---------------------------------------------------------------------------
// CookingMode — thin boundary wrapper
// A crash mid-cook must not blow away the app shell: CookErrorBoundary is a
// true ancestor of CookingModeInner (which holds all the hooks/logic below),
// so it can catch errors thrown anywhere during its render, including from
// hooks like useCookSession/useRecipe.
// ---------------------------------------------------------------------------

export function CookingMode(): JSX.Element {
  return (
    <CookErrorBoundary>
      <CookingModeInner />
    </CookErrorBoundary>
  );
}

function CookingModeInner(): JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const { me } = useAuth();
  const householdId =
    me?.user.currentHouseholdId ?? me?.memberships[0]?.householdId ?? "";

  const session = useCookSession(sessionId);
  const { recipe } = useRecipe(session?.recipeId);

  useWakeLock(true);

  const { cookingSplit, timer, patchPrefs } = usePrefs();
  const timers = useTimers(session ?? { activeTimers: [] });

  // Local UI state — must be declared before any conditional returns
  const [timerTrayOpen, setTimerTrayOpen] = useState(false);
  const [waitOpen, setWaitOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);

  // Loading guard
  if (!session || !recipe) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        Loading…
      </main>
    );
  }

  // ---------------------------------------------------------------------------
  // All code below runs only when session + recipe are both defined.
  // TypeScript narrows after the guard above, but closures don't re-narrow,
  // so we capture into stable locals with asserted types.
  // ---------------------------------------------------------------------------
  const s: CookSessionApiItem = session;
  const r: RecipeApiItem = recipe;

  const { running, fired, shortestRemainingMs, startTimer, extendTimer, stopTimer } = timers;

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const cookSteps = r.steps.filter((step) => step.kind !== "prep");

  const currentStep =
    r.steps.find((step) => step.id === s.currentStepId) ??
    r.steps.find(
      (step) => step.kind !== "prep" && s.stepStates[step.id]?.status !== "done",
    ) ??
    r.steps[0] ??
    null;

  const currentStepIdx = currentStep
    ? r.steps.findIndex((step) => step.id === currentStep.id)
    : -1;
  const canBack = currentStepIdx > 0;
  const canNext = currentStepIdx < r.steps.length - 1;

  const progress = progressOf(r.steps, s.stepStates, s.currentStepId);

  // ---------------------------------------------------------------------------
  // stepNumberFor — 1-based index among non-prep steps, or null
  // ---------------------------------------------------------------------------

  function stepNumberFor(stepId: string): number | null {
    const idx = cookSteps.findIndex((step) => step.id === stepId);
    return idx >= 0 ? idx + 1 : null;
  }

  // ---------------------------------------------------------------------------
  // Patch shorthand
  // ---------------------------------------------------------------------------

  function patch(body: Parameters<typeof sync.patchCookSessionOptimistic>[1]): void {
    void sync.patchCookSessionOptimistic(s.id, body);
  }

  // ---------------------------------------------------------------------------
  // Mutation handlers
  // ---------------------------------------------------------------------------

  function focusStep(stepId: string): void {
    patch({ currentStepId: stepId });
  }

  function toggleDone(stepId: string): void {
    const done = s.stepStates[stepId]?.status === "done";
    const next = {
      ...s.stepStates,
      [stepId]: done
        ? { status: "active" as const, startedAt: nowIso() }
        : { status: "done" as const, completedAt: nowIso() },
    };
    patch({ stepStates: next });
  }

  function doneStepAndAdvance(): void {
    if (!currentStep) return;
    const next = {
      ...s.stepStates,
      [currentStep.id]: { status: "done" as const, completedAt: nowIso() },
    };
    const after = r.steps.find(
      (step) => step.kind !== "prep" && step.id !== currentStep.id && next[step.id]?.status !== "done",
    );
    patch({ stepStates: next, currentStepId: after?.id ?? currentStep.id });
  }

  function gotoNext(): void {
    if (!canNext) return;
    patch({ currentStepId: r.steps[currentStepIdx + 1]!.id });
  }

  function gotoBack(): void {
    if (!canBack) return;
    patch({ currentStepId: r.steps[currentStepIdx - 1]!.id });
  }

  function handleStartTimer(label: string, minutes: number): void {
    const next = startTimer(label, currentStep?.id ?? "", minutes);
    patch({ activeTimers: next });
  }

  function handleExtendTimer(id: string, addMs: number): void {
    patch({ activeTimers: extendTimer(id, addMs) });
  }

  function handleStopTimer(id: string): void {
    patch({ activeTimers: stopTimer(id) });
  }

  function addNote(text: string): void {
    patch({
      notes: [
        ...s.notes,
        {
          id: `note_${ulid()}`,
          stepId: currentStep?.id ?? null,
          text,
          ts: nowIso(),
        },
      ],
    });
  }

  function toggleChecked(ingredientId: string): void {
    const has = s.checkedIngredients.includes(ingredientId);
    patch({
      checkedIngredients: has
        ? s.checkedIngredients.filter((x) => x !== ingredientId)
        : [...s.checkedIngredients, ingredientId],
    });
  }

  function startParallel(stepId: string): void {
    patch({
      stepStates: {
        ...s.stepStates,
        [stepId]: { status: "active" as const, startedAt: nowIso() },
      },
      currentStepId: stepId,
    });
  }

  function cycleSplit(): void {
    void patchPrefs({ cookingSplit: nextSplit(cookingSplit) });
  }

  function exit(): void {
    clearFocusedSessionId();
    void navigate("/");
  }

  async function pause(): Promise<void> {
    try {
      await sync.pauseCookSessionOptimistic(s.id);
      clearFocusedSessionId();
      void navigate("/");
    } catch (e) {
      toast.error("Failed to pause — try again.");
      console.error(e);
    }
  }

  async function abandon(): Promise<void> {
    try {
      await sync.abandonCookSessionOptimistic(s.id);
      clearFocusedSessionId();
      void navigate("/");
    } catch (e) {
      toast.error("Failed to abandon — try again.");
      console.error(e);
    }
  }

  async function startWait(body: { pauseUntilMinutes: number; reason: string }): Promise<void> {
    try {
      await sync.waitCookSessionOptimistic(s.id, body);
      clearFocusedSessionId();
      void navigate("/");
    } catch (e) {
      toast.error("Failed to start wait — try again.");
      console.error(e);
    }
  }

  async function complete(body: CompleteCookSessionRequest): Promise<void> {
    try {
      await sync.completeCookSessionOptimistic(s.id, body);
      // Plan 14: this session was started from a plan slot — mark it cooked.
      // Fire-and-forget AFTER the completion succeeds: plan-state is
      // best-effort bookkeeping, so a failure here must never surface as a
      // failed completion or block navigation to the summary screen.
      if (s.planRef) void markSlotCooked(s.planRef).catch(() => {});
      clearFocusedSessionId();
      void navigate(`/cook/${s.id}/summary`, { state: body });
    } catch (e) {
      toast.error("Failed to complete — try again.");
      console.error(e);
    }
  }

  // ---------------------------------------------------------------------------
  // Split sizing
  // ---------------------------------------------------------------------------

  const splitMap: Record<string, [string, string]> = {
    "70-30": ["flex-[7]", "flex-[3]"],
    "60-40": ["flex-[6]", "flex-[4]"],
    "40-60": ["flex-[4]", "flex-[6]"],
  };
  const [topFlex, bottomFlex] = splitMap[cookingSplit] ?? ["flex-[6]", "flex-[4]"];

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-screen bg-background text-foreground overflow-hidden">
      {/* ─── Header ─── */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-border shrink-0">
        <button
          type="button"
          aria-label="Exit cooking"
          onClick={exit}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 -ml-1 rounded min-h-[48px] min-w-[48px] flex items-center justify-center"
        >
          <span className="text-xl" aria-hidden="true">×</span>
        </button>

        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold truncate leading-tight">{r.title}</h1>
          <p className="text-xs text-muted-foreground">
            Step {progress.currentIndex} of {progress.total}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setCompleteOpen(true)}
          className="text-sm font-medium text-primary hover:text-primary/80 transition-colors min-h-[48px] px-2 flex items-center"
        >
          Finish
        </button>

        <CookMenu
          // rv: the per-step note + photo composers live in StepPane (the primary path).
          // The menu items redirect there rather than writing data; a future enhancement
          // can lift StepPane's composer state up so the menu opens it directly.
          onAddNote={() => toast("Tap the note button on the current step to add a note.")}
          onAddPhoto={() => toast("Tap the photo chip on the current step to add a photo.")}
          onPause={() => void pause()}
          onAbandon={() => void abandon()}
        />
      </header>

      {/* ─── Progress bar ─── */}
      <ProgressBar
        steps={r.steps}
        stepStates={s.stepStates}
        currentStepId={s.currentStepId}
        startedAt={s.startedAt}
        onFocusStep={focusStep}
        onToggleDone={toggleDone}
      />

      {/* ─── Prep panel ─── */}
      <PrepPanel
        steps={r.steps}
        stepStates={s.stepStates}
        onToggleDone={toggleDone}
      />

      {/* ─── Split content area ─── */}
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Top: Step pane */}
        <div className={`${topFlex} min-h-0 overflow-y-auto`}>
          {currentStep ? (
            <StepPane
              householdId={householdId}
              recipe={r}
              step={currentStep}
              allSteps={r.steps}
              stepStates={s.stepStates}
              onStartTimer={handleStartTimer}
              onAddNote={addNote}
              onStartParallel={startParallel}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              All steps complete
            </div>
          )}
        </div>

        {/* Divider */}
        <SplitDivider split={cookingSplit} onCycle={cycleSplit} />

        {/* Bottom: Ingredients pane */}
        <div className={`${bottomFlex} min-h-0 overflow-y-auto`}>
          <IngredientsPane
            recipe={r}
            step={currentStep}
            checkedIngredients={s.checkedIngredients}
            onToggleChecked={toggleChecked}
          />
        </div>
      </div>

      {/* ─── Bottom bar ─── */}
      {currentStep?.kind === "wait" ? (
        <div className="shrink-0 px-4 py-3 border-t border-border">
          <button
            type="button"
            onClick={() => setWaitOpen(true)}
            className="w-full bg-primary text-primary-foreground rounded-lg py-3 font-semibold min-h-[48px]"
          >
            Start the wait
          </button>
        </div>
      ) : (
        <CookBottomBar
          onBack={gotoBack}
          onDoneStep={doneStepAndAdvance}
          onNext={gotoNext}
          canBack={canBack}
          canNext={canNext}
        />
      )}

      {/* ─── Timer pill (floating) ─── */}
      {running.length > 0 && (
        <div className="fixed top-4 right-4 z-50">
          <TimerPill
            count={running.length}
            shortestRemainingMs={shortestRemainingMs}
            onOpen={() => setTimerTrayOpen(true)}
          />
        </div>
      )}

      {/* ─── Timer tray ─── */}
      <TimerTray
        open={timerTrayOpen}
        onOpenChange={setTimerTrayOpen}
        running={running}
        stepNumberFor={stepNumberFor}
        onExtend={handleExtendTimer}
        onStop={handleStopTimer}
      />

      {/* ─── Fired alarm stack ─── */}
      <FiredAlarmStack
        fired={fired}
        stepNumberFor={stepNumberFor}
        soundEnabled={timer.soundEnabled}
        vibrateEnabled={timer.vibrateEnabled}
        onSnooze={handleExtendTimer}
        onStop={handleStopTimer}
      />

      {/* ─── Wait picker modal ─── */}
      {waitOpen && currentStep && (
        <WaitPicker
          step={currentStep}
          onConfirm={(body) => {
            setWaitOpen(false);
            void startWait(body);
          }}
          onCancel={() => setWaitOpen(false)}
        />
      )}

      {/* ─── Completion modal ─── */}
      {completeOpen && (
        <CompletionModal
          onComplete={(body) => {
            setCompleteOpen(false);
            void complete(body as CompleteCookSessionRequest);
          }}
          onCancel={() => setCompleteOpen(false)}
        />
      )}
    </div>
  );
}
